/**
 * Login Page
 */

// Core imports
import React from 'react';
import PropTypes from 'prop-types';

// Style, SEO and settings
import Helmet from 'react-helmet';
import { Col, css, Grid, Row, up, styled, withTheme } from '@smooth-ui/core-sc';

// Additional Components/Containers
import LoadAsync from '_platform/src/utils/LoadAsync';
import PublicComponent from '_platform/src/utils/PublicComponent';
import getReturnUrlPath from '_platform/src/utils/getReturnUrlPath';
import FormStyles from 'components/FormStyles/FormStyles';

import { withSettings } from 'containers/WebApp/SettingsContext';
import bgImage1x from './images/bg-login.jpg';
import bgImage2x from './images/bg-login@2x.jpg';

const LoginProvider = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginProvider" */ '_platform/src/containers/LoginProvider/LoginProvider'
  )
);
const LoginForm = LoadAsync(() =>
  import(
    /* webpackChunkName: "loginForm" */ '_platform/src/components/LoginForm/LoginForm'
  )
);

const PageContainer = styled.div`
  align-items: center;
  background-color: #131d28;
  background-image: url(${bgImage1x});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: center;
  @media only screen and (min-device-pixel-ratio: 1.5),
    only screen and (min-resolution: 144dpi),
    only screen and (min-resolution: 1.5dppx) {
    background-color: #131d28;
    background-image: url(${bgImage2x});
  }
`;

const IntroContainer = styled.div`
  box-sizing: border-box;
  color: #fff;
  margin: 0.5rem 0;
  padding: 0;
  ${up(
    'lg',
    css`
      padding: 2rem;
    `
  )};
  h2 {
    font-size: 30px;
  }
  p {
    color: #fff;
  }
  a {
    color: #fff;
    &:visited {
      color: #fff;
    }
  }
`;

const LoginContainer = styled.div`
  max-width: 500px;
  border: 1px rgba(255, 255, 255, 0.4) solid;
  background: rgba(0, 0, 0, 0.4);
  padding: 1rem;
  margin: 0.5rem auto;

  h2 span {
    font-weight: normal;
  }

  .reset-password {
    a {
      font-size: 14px;
      font-weight: normal;
    }
  }

  ${up(
    'lg',
    css`
      min-width: 300px;
    `
  )};
`;

const LoginPage = ({ settings }) => (
  <PublicComponent
    redirectOnError={
      // Redirect to returnUrl, if it's not the same as loginPagePath
      (getReturnUrlPath !== settings.settingsApp.loginPagePath &&
        getReturnUrlPath) ||
      '/'
    }
  >
    <Helmet>
      <title>Login</title>
      {/*<meta name="description" content="Page description" />*/}
    </Helmet>

    <PageContainer className="page-container">
      <Grid>
        <IntroContainer>
          <Grid>
            <Row>
              <Col xs={12}>
                <LoginContainer>
                  <h2 className="text--center">
                    Welcome <span>to</span> HP Rewards
                  </h2>
                  <p className="text--center">
                    HP Rewards is designed to reward your Partner Businesses for
                    your sales of eligible HP products. The more eligible
                    products you sell, the more points you’ll receive!
                  </p>
                  <FormStyles className="text--white">
                    <LoginProvider disableMeta>
                      <LoginForm formLabels={{ labelUserName: 'Username' }} />
                    </LoginProvider>
                  </FormStyles>
                </LoginContainer>
              </Col>
            </Row>
          </Grid>
        </IntroContainer>
      </Grid>
    </PageContainer>
  </PublicComponent>
);

LoginPage.propTypes = {
  settings: PropTypes.object.isRequired,
};

export default withTheme(withSettings(LoginPage));
